var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useState } from "react";
import { Box, Divider } from "@mui/material";
import { CohortInsightsPageFilters } from "../Filters/Filters";
import StatusInfo from "../../components/StatusInfo/StatusInfo";
import DevelopmentOpportunitiesBar from "../../components/DevelopmentOpportunitiesBar";
import { DistributionSet, getDescriptionWithTexts, } from "../DonutChart/DistributionSet";
import BarChart from "../../components/BarChart/BarChart";
import { ImpactLevelFunctionMatrix } from "../LevelFunctionMatrix/Variants";
import FactorScoresSelection from "./FactorScoresSelection";
import BackToButton from "../../components/BackToButton";
import { getRoute } from "../../routes/getAllRoutes";
import FilteredOverviewPage from "../FilteredOverviewPage";
import { processOverviewData, transformeScoreData, useGetLeaderScoreCategoriesQuery, useGetLeadershipOverviewQuery, useGetLeadershipScoresQuery, useGetScoreStateQuery, useGetLookupsFiltersQuery, } from "../../store/api/core";
import { FilterContext } from "../../providers/FilterProvider";
import LeaderCount from "../LeaderCount";
import { useHeatmap } from "../../hooks/heatmapScores";
import { useLocation } from "react-router-dom";
import { Colors } from "../../common/cohortHelpers";
import { getColorDecider } from "../../common/action";
import styles from "./LeaderInsights.module.scss";
import { PaneSelector } from "../PaneSelector/PaneSelector";
import InfoIcon from "../../icons/InfoIcon";
import { DataRelations } from "./LeaderInsights";
import { DownloadableSection } from "../DownloadableSection/DownloadableSection";
var StyledMainBg = {
    background: "var(--grey-color-10)",
    gap: "32px",
    padding: "32px",
    display: "flex",
    flexDirection: "column",
};
var StyledCard = {
    padding: "32px",
    background: "var(--grey-gradient-start-color-10)",
    borderRadius: "25px",
};
var ChartStyle = {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "40px",
    width: "100%",
};
var StyledAvgText = {
    color: "var(--grey-color-70)",
    fontFamily: "var(--font-stack-full-medium)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "24px",
    backgroundColor: "var(--background-color)",
};
var StatusDistributionStyle = {
    display: "flex",
    padding: "32px",
    flex: 1,
    minWidth: "300px",
    alignItems: "flex-start",
    background: "var(--grey-gradient-start-color-10)",
    borderRadius: "10px",
};
var overallHeidricChartStyle = {
    display: "flex",
    flex: 2,
    minWidth: "300px",
    padding: "32px",
    alignItems: "center",
    background: "var(--grey-gradient-start-color-10)",
    borderRadius: "10px",
};
var statusDistriTitle = {
    color: "var(--neutral-3)",
    fontFamily: "var(--font-stack-full-medium)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "24px",
    marginBottom: "67px",
};
var heatmapTitle = {
    fontSize: "24px",
    fontWeight: "500",
    lineHeight: "32px",
    fontFamily: "var(--font-stack-full-medium)",
    marginBottom: "20px",
    color: "var(--grey-color-90)",
};
var barChartStyle = {
    display: "inline-block",
    position: "relative",
    width: "100%",
};
var getPaneSelections = function (lookups, scores) {
    if (!lookups || !scores) {
        return [];
    }
    return scores
        .filter(function (_a) {
        var function_id = _a.function_id;
        return function_id != "ALL";
    })
        .map(function (_a) {
        var function_id = _a.function_id, sub_functions = _a.sub_functions;
        return ({
            root: { key: function_id, label: lookups[function_id] || function_id },
            nested: (sub_functions === null || sub_functions === void 0 ? void 0 : sub_functions.filter(function (_a) {
                var sub_function_id = _a.sub_function_id;
                return sub_function_id != "ALL";
            }).map(function (_a) {
                var sub_function_id = _a.sub_function_id;
                return ({
                    key: sub_function_id,
                    label: lookups[sub_function_id] || sub_function_id,
                });
            })) || [],
        });
    });
};
var headerIconColor = "var(--bad-1)";
var colors = ["var(--bad-1)", "var(--neutral-1)", "var(--good-1)"];
var matchStatus = [
    {
        text: "Act",
        color: {
            border: "var(--bad-2)",
            background: "var(--bad-1)",
        },
    },
    {
        text: "Watch",
        color: {
            border: "var(--neutral-2)",
            background: "var(--neutral-1)",
        },
    },
    {
        text: "Maintain",
        color: {
            border: "var(--good-2)",
            background: "var(--good-1)",
        },
    },
];
var backgroundColors = ["var(--bad-1)", "var(--neutral-1)", "var(--good-1)"];
var backgroundBorderColors = [
    "var(--bad-2)",
    "var(--neutral-2)",
    "var(--good-2)",
];
var levels = [2, 13, 85];
export var LocalFactorScoresSelection = function (_a) {
    var skillGroup = _a.skillGroup;
    return (_jsx(FactorScoresSelection, { skillGroup: skillGroup, bucketHeading: "Distribution by status (%)", colors: colors, levels: levels, backgroundColors: backgroundColors, backgroundBorderColors: backgroundBorderColors }));
};
export var LocalPaneSelector = function (_a) {
    var _b, _c, _d, _e;
    var skillGroup = _a.skillGroup;
    var getFilters = useContext(FilterContext).getFilters;
    var lookups = useGetLookupsFiltersQuery({});
    var scores = useGetLeadershipScoresQuery({ filters: getFilters() });
    var rows = getPaneSelections(((_c = (_b = lookups === null || lookups === void 0 ? void 0 : lookups.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.lookupCodeMap) || {}, ((_e = (_d = scores === null || scores === void 0 ? void 0 : scores.data) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.functions) || []);
    var Pane = function (_a) {
        var _b, _c;
        var inner = _a.inner;
        if (!inner) {
            return (_jsx("div", __assign({ className: styles.centerVertically }, { children: _jsxs("div", __assign({ className: styles.centerHorizontally }, { children: [_jsx(InfoIcon, {}), _jsx("div", __assign({ className: styles.textWidthLimiter }, { children: "Select a function and sub-function to see key strengths and opportunities that are critical to delivering business objectives." }))] })) })));
        }
        return (_jsx("div", __assign({ className: styles.factorScoreSection }, { children: _jsxs(DownloadableSection, __assign({ filename: "Factor_Scores_Pane.png" }, { children: [_jsxs("div", __assign({ className: styles.factorScoreTitle }, { children: [((_c = (_b = lookups === null || lookups === void 0 ? void 0 : lookups.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.lookupCodeMap[inner]) || "", " scores by factor", _jsx("div", { className: styles.horizontalRule })] })), _jsx("div", __assign({ className: styles.factorScoreBars }, { children: _jsx(FactorScoresSelection, { skillGroup: skillGroup, colors: colors, bucketHeading: "Status Distribution", subFunction: inner, levels: levels, backgroundColors: backgroundColors, backgroundBorderColors: backgroundBorderColors }) }))] })) })));
    };
    return _jsx(PaneSelector, { rows: rows, pane: Pane });
};
var ScoreBySkillPage = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8;
    var location = useLocation();
    var _9 = useState(), avgScoreData = _9[0], setAvgScoreGroupData = _9[1];
    var getFilters = useContext(FilterContext).getFilters;
    var apiType = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.apiType) || "";
    var title = ((_b = location.state) === null || _b === void 0 ? void 0 : _b.title) || "";
    var titleGroupBy = "Groups with development opportunities";
    var leaderShipOverview = useGetLeadershipOverviewQuery({
        filters: getFilters(),
    });
    var scores = useGetLeadershipScoresQuery({ filters: getFilters() });
    var heidrickScoreCategories = useGetLeaderScoreCategoriesQuery({
        filters: getFilters(),
        score_type: apiType,
    });
    var filters = getFilters() || {};
    var arraysEmpty = Object.values(filters)
        .filter(Array.isArray)
        .every(function (arr) { return arr.length === 0; });
    var LocalLeaderCount = function (_a) {
        var _b, _c;
        var useOverview = _a.useOverview, countText = _a.countText;
        var getFilters = useContext(FilterContext).getFilters;
        var scores = useOverview({ filters: __assign({}, getFilters()) });
        var totalNumberCount = (_c = (_b = scores === null || scores === void 0 ? void 0 : scores.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.leaders;
        return (_jsx(LeaderCount, { leadersCount: totalNumberCount, countText: countText }));
    };
    useEffect(function () {
        var _a, _b;
        if ((_b = (_a = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.averageScoreBySkillGroup) {
            var averageScoreGroup = matchStatus === null || matchStatus === void 0 ? void 0 : matchStatus.map(function (item, key) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                return (__assign(__assign({}, (_e = (_d = (_c = (_b = (_a = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.averageScoreBySkillGroup) === null || _c === void 0 ? void 0 : _c[apiType]) === null || _d === void 0 ? void 0 : _d.score_levels) === null || _e === void 0 ? void 0 : _e[key]), { text: item.text, count: (_l = (_k = (_j = (_h = (_g = (_f = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.averageScoreBySkillGroup) === null || _h === void 0 ? void 0 : _h[apiType]) === null || _j === void 0 ? void 0 : _j.score_levels) === null || _k === void 0 ? void 0 : _k[key]) === null || _l === void 0 ? void 0 : _l.count, color: item.color }));
            });
            setAvgScoreGroupData(averageScoreGroup);
        }
    }, [(_c = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _c === void 0 ? void 0 : _c.data]);
    var notHold = function (data) { return ({
        data: data,
    }); };
    var useSeparateScores = function () {
        return useHeatmap(function () { var _a; return notHold(processOverviewData((_a = leaderShipOverview === null || leaderShipOverview === void 0 ? void 0 : leaderShipOverview.data) === null || _a === void 0 ? void 0 : _a.data)); }, function () { return notHold(transformeScoreData(scores === null || scores === void 0 ? void 0 : scores.data)); }, function () { return notHold({}); }, function () { return ({ state: "nodata" }); }, apiType, apiType);
    };
    var preParedData = function () {
        var _a, _b, _c, _d;
        var data = (_d = (_c = (_b = (_a = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.averageScoreBySkillGroup) === null || _c === void 0 ? void 0 : _c.scores) === null || _d === void 0 ? void 0 : _d.filter(function (item) { return item.leaderCount > 0; });
        return data;
    };
    var minScore = Array.isArray((_f = (_e = (_d = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.averageScoreBySkillGroup) === null || _f === void 0 ? void 0 : _f.scores) &&
        ((_h = (_g = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.averageScoreBySkillGroup.scores.length) > 0
        ? Math.min.apply(Math, (((_m = (_l = (_k = (_j = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.averageScoreBySkillGroup) === null || _l === void 0 ? void 0 : _l.scores) === null || _m === void 0 ? void 0 : _m.map(function (item) { return item.score; })) || [])) || 0
        : 0;
    var maxScore = Array.isArray((_q = (_p = (_o = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _o === void 0 ? void 0 : _o.data) === null || _p === void 0 ? void 0 : _p.averageScoreBySkillGroup) === null || _q === void 0 ? void 0 : _q.scores) &&
        ((_s = (_r = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _r === void 0 ? void 0 : _r.data) === null || _s === void 0 ? void 0 : _s.averageScoreBySkillGroup.scores.length) > 0
        ? Math.max.apply(Math, (((_w = (_v = (_u = (_t = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _t === void 0 ? void 0 : _t.data) === null || _u === void 0 ? void 0 : _u.averageScoreBySkillGroup) === null || _v === void 0 ? void 0 : _v.scores) === null || _w === void 0 ? void 0 : _w.map(function (item) { return item.score; })) || [])) || 0
        : 100;
    var step = 5; // or 5, as needed
    // Generate xTicks dynamically
    var xTicks = Array.from({ length: Math.floor((maxScore - minScore) / step) + 1 }, function (_, i) { return minScore + i * step; });
    return (_jsxs(_Fragment, { children: [_jsx(FilteredOverviewPage, { availableFilters: CohortInsightsPageFilters, title: titleGroupBy, secondaryHeadingTitle: title, rightTrailer: _jsx(LocalLeaderCount, { useOverview: useGetScoreStateQuery, countText: "leader" }), backgroundImage: getRoute("HeaderBackground"), backToButton: _jsx(BackToButton, { title: "Cohort Insights", backUrl: getRoute("LeaderInsights") }) }), _jsxs(Box, __assign({ sx: StyledMainBg }, { children: [_jsx(Box, __assign({ className: styles.statusCardContainer }, { children: _jsx(StatusInfo, { heading: "Status", data: [
                                {
                                    text: "Act ( < 80% )",
                                    bgColor: "var(--bad-1)",
                                    brColor: "var(--bad-2)",
                                },
                                {
                                    text: "Watch ( \u2265 80%; \u2264 90% )",
                                    bgColor: "var(--neutral-1)",
                                    brColor: "var(--neutral-2)",
                                },
                                {
                                    text: "Maintain ( > 90% )",
                                    bgColor: "var(--good-1)",
                                    brColor: "var(--good-2)",
                                },
                            ] }) })), _jsxs(Box, __assign({ sx: ChartStyle }, { children: [_jsx(Box, __assign({ sx: StatusDistributionStyle }, { children: _jsx(DownloadableSection, __assign({ filename: "Donut-chart-skills-heidrick-score" }, { children: _jsxs(Box, { children: [_jsx(Box, __assign({ sx: statusDistriTitle }, { children: "Status Distribution" })), _jsx(DistributionSet, { label: "Leaders", radius: 100, margin: 70, buckets: getDescriptionWithTexts(avgScoreData ? avgScoreData : []) })] }) })) })), _jsx(Box, __assign({ sx: overallHeidricChartStyle }, { children: _jsx(DownloadableSection, __assign({ filename: "Bar-chart-skills-heidrick-score" }, { children: _jsx(Box, __assign({ sx: barChartStyle }, { children: _jsx(BarChart, { colors: Colors["insights"], title: "Spread of Overall ".concat(title, " (%)"), xTicks: xTicks, formatAverageScore: function (value) { return ({
                                                text: "".concat(value, "%"),
                                                formatting: {
                                                    paddingLeft: "6px",
                                                },
                                            }); }, getScoreDescriptionText: function (score) {
                                                var colorDecider = getColorDecider(Colors["insights"]);
                                                return colorDecider(score).description.all;
                                            }, range: {
                                                minimum: Array.isArray((_z = (_y = (_x = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _x === void 0 ? void 0 : _x.data) === null || _y === void 0 ? void 0 : _y.averageScoreBySkillGroup) === null || _z === void 0 ? void 0 : _z.scores) &&
                                                    heidrickScoreCategories.data.data.averageScoreBySkillGroup
                                                        .scores.length > 0
                                                    ? Math.min.apply(Math, heidrickScoreCategories.data.data.averageScoreBySkillGroup.scores.map(function (item) { return item.score - 2; })) : 0,
                                                maximum: Array.isArray((_2 = (_1 = (_0 = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _0 === void 0 ? void 0 : _0.data) === null || _1 === void 0 ? void 0 : _1.averageScoreBySkillGroup) === null || _2 === void 0 ? void 0 : _2.scores) &&
                                                    heidrickScoreCategories.data.data.averageScoreBySkillGroup
                                                        .scores.length > 0
                                                    ? Math.max.apply(Math, heidrickScoreCategories.data.data.averageScoreBySkillGroup.scores.map(function (item) { return item.score + 2; })) : 100,
                                            }, data: {
                                                axisData: preParedData() || [],
                                                averageScore: Math.round((_5 = (_4 = (_3 = heidrickScoreCategories === null || heidrickScoreCategories === void 0 ? void 0 : heidrickScoreCategories.data) === null || _3 === void 0 ? void 0 : _3.data) === null || _4 === void 0 ? void 0 : _4.averageScoreBySkillGroup) === null || _5 === void 0 ? void 0 : _5.overallAverageScore),
                                                targetScore: 2.2,
                                                showTarget: false,
                                            } }) })) })) }))] })), _jsx(Box, __assign({ sx: StyledCard }, { children: arraysEmpty && (_jsx(DevelopmentOpportunitiesBar, { title: titleGroupBy, headerIconColor: headerIconColor, colors: colors
                                ? colors
                                : ["var(--bad-1)", "var(--neutral-1)", "var(--good-1)"], data: ((_6 = location.state) === null || _6 === void 0 ? void 0 : _6.groupsByData) || [] })) })), _jsx(Box, __assign({ sx: StyledCard }, { children: _jsxs(DownloadableSection, __assign({ filename: "Bar-chart-skills-heidrick-score" }, { children: [_jsx(Box, __assign({ sx: heatmapTitle }, { children: "".concat(title, " Score by level and function") })), ((_7 = leaderShipOverview === null || leaderShipOverview === void 0 ? void 0 : leaderShipOverview.data) === null || _7 === void 0 ? void 0 : _7.data) !== undefined &&
                                    (scores === null || scores === void 0 ? void 0 : scores.data) !== undefined ? (_jsx(ImpactLevelFunctionMatrix, { useHeatmapScores: useSeparateScores, riskRole: apiType })) : null] })) })), _jsx(Box, __assign({ sx: StyledCard }, { children: _jsx(DownloadableSection, __assign({ filename: "Factor_Scores_By_Factor.png" }, { children: _jsxs(Box, { children: [_jsx(Box, __assign({ sx: {
                                            display: "flex",
                                            justifyContent: "space-between",
                                        } }, { children: _jsx(Box, __assign({ sx: __assign(__assign({}, StyledAvgText), { fontSize: "24px", fontWeight: 500, lineHeight: "32px", color: "var(--grey-color-90)" }) }, { children: "".concat(title, " scores by factor") })) })), _jsx(Box, { children: _jsx(Divider, { sx: { margin: "32px 0" } }) }), _jsx(Box, { children: ((_8 = DataRelations[title]) === null || _8 === void 0 ? void 0 : _8.factorChart) || _jsx(_Fragment, {}) })] }) })) }))] }))] }));
};
export { ScoreBySkillPage };
